import React, { useState, useEffect, useCallback, useMemo } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import {
  Calculator, Upload, AlertTriangle, Wand2, Edit, Eye, Plus, Check, Info, FileText, X, RefreshCw, Zap, Clock, ShieldCheck, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { useActiveCompany } from '../hooks/useActiveCompany';
import BulkUploadModal from '../components/BulkUploadModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CTCPlanner = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [structures, setStructures] = useState([]);
  const [selectedStructure, setSelectedStructure] = useState('');
  const [ctcAmount, setCtcAmount] = useState('');
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employeeCTCs, setEmployeeCTCs] = useState([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkStructure, setBulkStructure] = useState('');
  const [unmapped, setUnmapped] = useState({ unmapped_count: 0, default_structure_code: null, default_structure_name: null });
  const [backfilling, setBackfilling] = useState(false);

  // Manual Salary Structure State
  const [salaryHeads, setSalaryHeads] = useState([]);
  const [manualAmounts, setManualAmounts] = useState({});
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split('T')[0]);
  const [manualRemarks, setManualRemarks] = useState('');
  const [savingManual, setSavingManual] = useState(false);
  const [headSearch, setHeadSearch] = useState('');

  // CTC History / Detail Modal State
  const [viewingCTC, setViewingCTC] = useState(null);
  const [ctcHistory, setCtcHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const isManualMode = selectedStructure === 'MANUAL';

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const fetchEmployeeCTCs = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/payroll/employee-ctc?company_id=${selectedCompany}`, { withCredentials: true });
      setEmployeeCTCs(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  const fetchEmployees = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true });
      setEmployees(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  const fetchStructures = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/payroll/structures?company_id=${selectedCompany}`, { withCredentials: true });
      setStructures(data);
      const preferred = data.find(s => s.is_default) || data[0];
      if (preferred && !bulkStructure) setBulkStructure(preferred.code);
    } catch (e) { console.error(e); }
  }, [bulkStructure, selectedCompany]);

  const fetchSalaryHeads = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      // Fetch active salary heads from masters
      const res1 = await axios.get(`${API_URL}/masters/all?company_id=${selectedCompany}`, { withCredentials: true }).catch(() => ({ data: {} }));
      const res2 = await axios.get(`${API_URL}/payroll/salary-heads?company_id=${selectedCompany}`, { withCredentials: true }).catch(() => ({ data: [] }));
      
      const masterHeads = res1.data?.salary_heads || [];
      const configHeads = Array.isArray(res2.data) ? res2.data : [];

      // Merge and deduplicate by head code
      const headMap = {};

      // Seed standard default heads if empty
      const defaultStandardHeads = [
        { code: 'BASIC', name: 'Basic Salary', head_type: 'earning', display_order: 1 },
        { code: 'HRA', name: 'House Rent Allowance (HRA)', head_type: 'earning', display_order: 2 },
        { code: 'SPECIAL_ALLOWANCE', name: 'Special Allowance', head_type: 'earning', display_order: 3 },
        { code: 'CONVEYANCE', name: 'Conveyance Allowance', head_type: 'earning', display_order: 4 },
        { code: 'MEDICAL', name: 'Medical Allowance', head_type: 'earning', display_order: 5 },
        { code: 'EDUCATION_ALLOWANCE', name: 'Education Allowance', head_type: 'earning', display_order: 6 },
        { code: 'WASHING_ALLOWANCE', name: 'Washing Allowance', head_type: 'earning', display_order: 7 },
        { code: 'TELEPHONE', name: 'Telephone / Mobile Allowance', head_type: 'earning', display_order: 8 },
        { code: 'FOOD_ALLOWANCE', name: 'Food Allowance / Coupons', head_type: 'earning', display_order: 9 },
        { code: 'DRIVER_ALLOWANCE', name: 'Driver Allowance', head_type: 'earning', display_order: 10 },
        { code: 'UNIFORM_ALLOWANCE', name: 'Uniform Allowance', head_type: 'earning', display_order: 11 },
        { code: 'OTHER_ALLOWANCE', name: 'Other Allowance', head_type: 'earning', display_order: 12 },
        { code: 'BONUS', name: 'Statutory Bonus', head_type: 'earning', display_order: 13 },
        { code: 'INCENTIVE', name: 'Performance Incentive', head_type: 'earning', display_order: 14 },
        { code: 'ARREAR', name: 'Salary Arrears', head_type: 'earning', display_order: 15 },
        { code: 'PF_EMPLOYER', name: 'Employer PF Contribution (12%)', head_type: 'employer_contribution', display_order: 20 },
        { code: 'ESI_EMPLOYER', name: 'Employer ESI Contribution (3.25%)', head_type: 'employer_contribution', display_order: 21 },
        { code: 'PF_EMPLOYEE', name: 'Employee PF Deduction (12%)', head_type: 'deduction', display_order: 30 },
        { code: 'ESI_EMPLOYEE', name: 'Employee ESI Deduction (0.75%)', head_type: 'deduction', display_order: 31 },
        { code: 'PT', name: 'Professional Tax (PT)', head_type: 'deduction', display_order: 32 },
        { code: 'LWF', name: 'Labour Welfare Fund (LWF)', head_type: 'deduction', display_order: 33 },
        { code: 'TDS', name: 'Income Tax (TDS)', head_type: 'deduction', display_order: 34 }
      ];

      defaultStandardHeads.forEach(h => { headMap[h.code] = h; });

      masterHeads.forEach(h => {
        if (h.code) {
          headMap[h.code] = {
            code: h.code,
            name: h.name || h.code,
            head_type: (h.head_type || 'earning').toLowerCase(),
            display_order: h.display_order || 50,
            is_active: h.is_active !== false
          };
        }
      });

      configHeads.forEach(h => {
        if (h.code) {
          headMap[h.code] = {
            code: h.code,
            name: h.name || h.code,
            head_type: (h.head_type || 'earning').toLowerCase(),
            display_order: h.display_order || 50,
            is_active: h.is_active !== false
          };
        }
      });

      const sortedList = Object.values(headMap).sort((a, b) => (a.display_order || 99) - (b.display_order || 99));
      setSalaryHeads(sortedList);
    } catch (e) {
      console.error("Error fetching salary heads:", e);
    }
  }, [selectedCompany]);

  const fetchUnmapped = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/payroll/employee-ctc/unmapped-count?company_id=${selectedCompany}`, { withCredentials: true });
      setUnmapped(data);
    } catch (e) { /* non-fatal */ }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      fetchStructures();
      fetchEmployees();
      fetchEmployeeCTCs();
      fetchUnmapped();
      fetchSalaryHeads();
    }
  }, [selectedCompany, fetchEmployeeCTCs, fetchEmployees, fetchStructures, fetchUnmapped, fetchSalaryHeads]);

  // Handle Head Amount Changes in Manual Mode
  const handleManualAmountChange = (code, val) => {
    setManualAmounts(prev => ({
      ...prev,
      [code]: val
    }));
  };

  // Real-time calculation for Manual Salary Structure
  const manualCalculations = useMemo(() => {
    let totalEarningsMonthly = 0;
    let totalDeductionsMonthly = 0;
    let totalEmployerMonthly = 0;

    const components = [];

    salaryHeads.forEach(h => {
      const rawVal = manualAmounts[h.code];
      const amt = parseFloat(rawVal) || 0;
      if (amt !== 0 || rawVal !== undefined) {
        const type = (h.head_type || 'earning').toLowerCase();
        const annualAmt = Math.round(amt * 12);
        
        components.push({
          head_code: h.code,
          head_name: h.name,
          head_type: type,
          monthly_amount: amt,
          annual_amount: annualAmt,
          calculation_type: 'manual'
        });

        if (type === 'earning') {
          totalEarningsMonthly += amt;
        } else if (type === 'deduction') {
          totalDeductionsMonthly += amt;
        } else if (type === 'employer_contribution') {
          totalEmployerMonthly += amt;
        }
      }
    });

    const grossMonthly = Math.round(totalEarningsMonthly);
    const grossAnnual = Math.round(grossMonthly * 12);
    const employerMonthly = Math.round(totalEmployerMonthly);
    const employerAnnual = Math.round(employerMonthly * 12);
    const ctcMonthly = Math.round(grossMonthly + employerMonthly);
    const ctcAnnual = Math.round(ctcMonthly * 12);
    const deductionsMonthly = Math.round(totalDeductionsMonthly);
    const netMonthly = Math.round(grossMonthly - deductionsMonthly);

    return {
      grossMonthly,
      grossAnnual,
      deductionsMonthly,
      employerMonthly,
      employerAnnual,
      ctcMonthly,
      ctcAnnual,
      netMonthly,
      components
    };
  }, [salaryHeads, manualAmounts]);

  // Auto-fill Statutory Defaults helper
  const handleAutoFillStatutory = () => {
    const basicAmt = parseFloat(manualAmounts['BASIC'] || 0);
    if (!basicAmt) {
      toast.error('Please enter a Basic Salary amount first.');
      return;
    }
    const grossEst = manualCalculations.grossMonthly || basicAmt;

    // PF 12% capped at ₹1,800/mo (PF base max 15,000)
    const pfBase = Math.min(basicAmt, 15000);
    const pfMonthly = Math.round(pfBase * 0.12);

    // ESI 0.75% emp / 3.25% er if gross <= 21,000
    const esiEmp = grossEst <= 21000 ? Math.round(grossEst * 0.0075) : 0;
    const esiEr = grossEst <= 21000 ? Math.round(grossEst * 0.0325) : 0;

    // PT estimate
    let ptEst = 0;
    if (grossEst > 20000) ptEst = 200;
    else if (grossEst > 15000) ptEst = 150;

    setManualAmounts(prev => ({
      ...prev,
      'PF_EMPLOYEE': prev['PF_EMPLOYEE'] !== undefined ? prev['PF_EMPLOYEE'] : pfMonthly,
      'PF_EMPLOYER': prev['PF_EMPLOYER'] !== undefined ? prev['PF_EMPLOYER'] : pfMonthly,
      'ESI_EMPLOYEE': prev['ESI_EMPLOYEE'] !== undefined ? prev['ESI_EMPLOYEE'] : esiEmp,
      'ESI_EMPLOYER': prev['ESI_EMPLOYER'] !== undefined ? prev['ESI_EMPLOYER'] : esiEr,
      'PT': prev['PT'] !== undefined ? prev['PT'] : ptEst,
    }));
    toast.success('Statutory PF, ESI & PT amounts estimated and filled!');
  };

  const handleBackfill = async () => {
    if (!unmapped.default_structure_code) {
      toast.error('No default structure in Masters. Mark one structure as default first.');
      return;
    }
    setBackfilling(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/payroll/employee-ctc/backfill-structure?company_id=${selectedCompany}&structure_code=${unmapped.default_structure_code}`,
        {}, { withCredentials: true }
      );
      toast.success(`${data.updated_count} CTC row(s) mapped to ${data.applied_structure_code}. Payroll will now show the full breakup.`);
      fetchEmployeeCTCs(); fetchUnmapped();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally { setBackfilling(false); }
  };

  const handleCalculate = async () => {
    if (!selectedStructure || selectedStructure === 'MANUAL' || !ctcAmount) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/payroll/ctc/calculate?company_id=${selectedCompany}&structure_id=${selectedStructure}&ctc_annual=${ctcAmount}`, {}, { withCredentials: true });
      setBreakdown(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Calculation error'); } finally { setLoading(false); }
  };

  const handleAssignCTC = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }

    if (isManualMode) {
      if (manualCalculations.components.length === 0 || manualCalculations.grossMonthly <= 0) {
        toast.error('Please enter at least one earning head amount (e.g. Basic Salary).');
        return;
      }
      setSavingManual(true);
      try {
        await axios.post(`${API_URL}/payroll/employee-ctc`, {
          employee_id: selectedEmployee,
          company_id: selectedCompany,
          structure_id: null,
          structure_code: 'MANUAL',
          structure_source: 'manual',
          ctc_annual: manualCalculations.ctcAnnual,
          ctc_monthly: manualCalculations.ctcMonthly,
          gross_annual: manualCalculations.grossAnnual,
          gross_monthly: manualCalculations.grossMonthly,
          net_monthly: manualCalculations.netMonthly,
          total_deductions_monthly: manualCalculations.deductionsMonthly,
          total_employer_monthly: manualCalculations.employerMonthly,
          components: manualCalculations.components,
          effective_from: effectiveFrom,
          remarks: manualRemarks || 'Manual Salary Structure Assignment'
        }, { withCredentials: true });

        toast.success('Manual Salary Structure assigned successfully!');
        setSelectedEmployee('');
        setManualAmounts({});
        setManualRemarks('');
        fetchEmployeeCTCs();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Error saving manual CTC');
      } finally {
        setSavingManual(false);
      }
    } else {
      if (!breakdown) return;
      try {
        await axios.post(`${API_URL}/payroll/employee-ctc`, {
          employee_id: selectedEmployee,
          company_id: selectedCompany,
          structure_id: selectedStructure,
          structure_code: structures.find(s => s.id === selectedStructure)?.code || 'STD',
          structure_source: 'group',
          ctc_annual: breakdown.ctc_annual,
          ctc_monthly: breakdown.ctc_monthly,
          gross_annual: breakdown.gross_annual,
          gross_monthly: breakdown.gross_monthly,
          net_monthly: breakdown.net_monthly,
          total_deductions_monthly: breakdown.total_deductions_monthly || 0,
          total_employer_monthly: breakdown.total_employer_monthly || 0,
          components: breakdown.components.map((c) => ({
            head_code: c.head_code,
            head_name: c.head_name,
            head_type: c.head_type,
            monthly_amount: c.monthly_amount,
            annual_amount: c.annual_amount,
            calculation_type: c.calculation_type
          })),
          effective_from: effectiveFrom,
        }, { withCredentials: true });
        toast.success('CTC assigned successfully!');
        setSelectedEmployee('');
        fetchEmployeeCTCs();
      } catch (e) { toast.error(e.response?.data?.detail || 'Error assigning CTC'); }
    }
  };

  // Open Detailed View & History Modal for a recent CTC assignment
  const handleOpenCTCDetails = async (ctcRow) => {
    setViewingCTC(ctcRow);
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(`${API_URL}/payroll/employee-ctc/history/${ctcRow.employee_id}`, { withCredentials: true });
      setCtcHistory(data || []);
    } catch (e) {
      setCtcHistory([ctcRow]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Populate Manual mode from existing row for edit/revision
  const handlePopulateManualForEdit = (ctcRow) => {
    setSelectedStructure('MANUAL');
    setSelectedEmployee(ctcRow.employee_id);
    const newAmounts = {};
    (ctcRow.components || []).forEach(c => {
      if (c.head_code) {
        newAmounts[c.head_code] = c.monthly_amount;
      }
    });
    setManualAmounts(newAmounts);
    setManualRemarks(`Revision of previous structure effective from ${ctcRow.effective_from}`);
    setViewingCTC(null);
    toast.info(`Populated Manual Structure entry for ${ctcRow.employee_code || 'employee'}. Adjust amounts and save as new version.`);
  };

  const filteredHeads = useMemo(() => {
    if (!headSearch.trim()) return salaryHeads;
    const q = headSearch.toLowerCase();
    return salaryHeads.filter(h => h.name.toLowerCase().includes(q) || h.code.toLowerCase().includes(q));
  }, [salaryHeads, headSearch]);

  const earningsHeads = filteredHeads.filter(h => (h.head_type || 'earning').toLowerCase() === 'earning');
  const deductionHeads = filteredHeads.filter(h => (h.head_type || 'earning').toLowerCase() === 'deduction');
  const employerHeads = filteredHeads.filter(h => (h.head_type || 'earning').toLowerCase() === 'employer_contribution');

  return (
    <MainLayout>
      <div data-testid="ctc-planner-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">CTC Planner</h1>
            <p className="text-sm text-slate-500 mt-1">Calculate, customize manually, and assign Cost-to-Company breakdowns</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="ctc-bulk-assign-btn"
              onClick={() => setShowBulkUpload(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
            >
              <Upload className="w-4 h-4" /> Bulk Assign CTC
            </button>
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white shadow-sm font-medium text-slate-700">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        {/* Diagnostic banner */}
        {unmapped.unmapped_count > 0 && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="ctc-unmapped-banner">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {unmapped.unmapped_count} CTC row{unmapped.unmapped_count > 1 ? 's are' : ' is'} missing a salary structure mapping.
              </p>
              <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                Without a structure, the payroll register cannot bifurcate the salary into Basic / HRA / PF / TDS etc — it will show blank earnings and deductions.
                Map them to the default structure{unmapped.default_structure_code ? <> <span className="font-mono font-semibold">{unmapped.default_structure_code}</span> ({unmapped.default_structure_name})</> : ''} to fix this in one click.
              </p>
            </div>
            <button
              data-testid="ctc-backfill-btn"
              disabled={backfilling || !unmapped.default_structure_code}
              onClick={handleBackfill}
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 shrink-0"
            >
              <Wand2 className="w-3.5 h-3.5" />
              {backfilling ? 'Mapping…' : `Auto-map to ${unmapped.default_structure_code || 'default'}`}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Controls & Mode Selection Panel */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
            <h2 className="text-base font-semibold text-slate-800 flex items-center justify-between">
              <span>Salary Structure Selection</span>
              {isManualMode && <span className="text-xs bg-purple-100 text-purple-700 font-semibold px-2 py-0.5 rounded-full border border-purple-200">Manual Mode</span>}
            </h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Salary Structure Option</label>
              <select
                data-testid="ctc-structure-select"
                value={selectedStructure}
                onChange={(e) => {
                  setSelectedStructure(e.target.value);
                  setBreakdown(null);
                }}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-medium"
              >
                <option value="">-- Select Salary Structure --</option>
                {structures.map((s) => (
                  <option key={s.id} value={s.id}>{`${s.name} (${s.code})`}</option>
                ))}
                <option value="MANUAL" className="font-semibold text-purple-700">✨ Manual (Define Custom Head Breakup)</option>
              </select>
            </div>

            {!isManualMode ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Annual CTC (₹)</label>
                  <input
                    data-testid="ctc-amount-input"
                    type="number"
                    value={ctcAmount}
                    onChange={(e) => setCtcAmount(e.target.value)}
                    placeholder="e.g. 1200000"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </div>
                <button
                  data-testid="calculate-ctc-btn"
                  onClick={handleCalculate}
                  disabled={loading || !selectedStructure || !ctcAmount}
                  className="w-full flex items-center justify-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#0040CC] transition-colors disabled:opacity-50 shadow-sm"
                >
                  <Calculator className="w-4 h-4" /><span>{loading ? 'Calculating...' : 'Calculate Breakdown'}</span>
                </button>
              </>
            ) : (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-900 space-y-2">
                <p className="font-semibold flex items-center gap-1.5"><Info className="w-4 h-4 text-purple-600" /> Manual Structure Mode Active</p>
                <p className="leading-relaxed text-purple-800">
                  Predefined salary rules are bypassed. Enter monthly amounts per salary head on the right. Fixed Gross, Monthly & Annual CTC update in real time.
                </p>
                <button
                  onClick={handleAutoFillStatutory}
                  className="w-full mt-2 flex items-center justify-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white font-medium py-1.5 rounded text-xs shadow-sm transition-colors"
                >
                  <Zap className="w-3.5 h-3.5" /> Auto-fill Statutory PF/ESI/PT
                </button>
              </div>
            )}

            {/* Employee Assignment Form */}
            <div className="pt-4 border-t border-slate-200 space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">Assign to Employee</h3>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Select Employee</label>
                <select
                  data-testid="ctc-employee-select"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">-- Select Employee --</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{`${emp.personal_details?.first_name} ${emp.personal_details?.last_name} (${emp.employment_details?.employee_code})`}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Effective From Date</label>
                <input
                  type="date"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                />
              </div>

              {isManualMode && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Revision / Structure Remarks</label>
                  <input
                    type="text"
                    value={manualRemarks}
                    onChange={(e) => setManualRemarks(e.target.value)}
                    placeholder="e.g. Special offer / manual revision"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}

              <button
                data-testid="assign-ctc-btn"
                onClick={handleAssignCTC}
                disabled={!selectedEmployee || (isManualMode ? (manualCalculations.grossMonthly <= 0 || savingManual) : !breakdown)}
                className="w-full bg-emerald-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 shadow-sm flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                {savingManual ? 'Saving Manual CTC...' : isManualMode ? 'Save & Assign Manual CTC' : 'Assign Template CTC'}
              </button>
            </div>
          </div>

          {/* Main Display / Manual Entry Grid */}
          <div className="lg:col-span-2 space-y-6">
            {/* MANUAL MODE DISPLAY */}
            {isManualMode ? (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-purple-50 via-white to-purple-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-base font-bold text-slate-900">Manual Salary Breakup Entry</h3>
                      <p className="text-xs text-slate-500">Enter monthly head amounts below. Totals calculate dynamically.</p>
                    </div>
                    <div className="w-48">
                      <input
                        type="text"
                        placeholder="Search salary head..."
                        value={headSearch}
                        onChange={(e) => setHeadSearch(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-2.5 py-1 text-xs outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>

                  {/* Dynamic Calculation Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-2">
                    <div className="bg-white p-2.5 border border-slate-200 rounded-lg text-center shadow-xs">
                      <p className="text-[11px] text-slate-500 uppercase font-medium">Fixed Gross</p>
                      <p className="text-base font-bold text-emerald-600 font-mono">₹{manualCalculations.grossMonthly.toLocaleString('en-IN')}</p>
                      <p className="text-[10px] text-slate-400 font-mono">₹{manualCalculations.grossAnnual.toLocaleString('en-IN')}/yr</p>
                    </div>
                    <div className="bg-white p-2.5 border border-slate-200 rounded-lg text-center shadow-xs">
                      <p className="text-[11px] text-slate-500 uppercase font-medium">Employee Ded.</p>
                      <p className="text-base font-bold text-red-500 font-mono">₹{manualCalculations.deductionsMonthly.toLocaleString('en-IN')}</p>
                      <p className="text-[10px] text-slate-400 font-mono">Employee Deductions</p>
                    </div>
                    <div className="bg-white p-2.5 border border-slate-200 rounded-lg text-center shadow-xs">
                      <p className="text-[11px] text-slate-500 uppercase font-medium">Employer Contrib.</p>
                      <p className="text-base font-bold text-blue-600 font-mono">₹{manualCalculations.employerMonthly.toLocaleString('en-IN')}</p>
                      <p className="text-[10px] text-slate-400 font-mono">PF + ESI Employer</p>
                    </div>
                    <div className="bg-white p-2.5 border border-purple-200 rounded-lg text-center shadow-xs bg-purple-50/50">
                      <p className="text-[11px] text-purple-700 uppercase font-medium">Monthly CTC</p>
                      <p className="text-base font-bold text-purple-700 font-mono">₹{manualCalculations.ctcMonthly.toLocaleString('en-IN')}</p>
                      <p className="text-[10px] text-purple-500 font-mono">₹{manualCalculations.ctcAnnual.toLocaleString('en-IN')}/yr</p>
                    </div>
                    <div className="bg-white p-2.5 border border-slate-200 rounded-lg text-center shadow-xs">
                      <p className="text-[11px] text-slate-500 uppercase font-medium">Net Monthly</p>
                      <p className="text-base font-bold text-[#0052FF] font-mono">₹{manualCalculations.netMonthly.toLocaleString('en-IN')}</p>
                      <p className="text-[10px] text-slate-400 font-mono">Gross - Deductions</p>
                    </div>
                  </div>
                </div>

                <div className="max-h-[520px] overflow-y-auto divide-y divide-slate-100">
                  {/* Earnings Section */}
                  <div className="p-4 bg-slate-50/50">
                    <p className="text-xs uppercase font-semibold text-emerald-800 mb-2 tracking-wider">Earnings ({earningsHeads.length} heads)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {earningsHeads.map(h => (
                        <div key={h.code} className="flex items-center justify-between bg-white p-2 border border-slate-200 rounded-lg">
                          <div className="min-w-0 pr-2">
                            <p className="text-xs font-semibold text-slate-800 truncate">{h.name}</p>
                            <p className="text-[10px] font-mono text-slate-400">{h.code}</p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className="text-xs text-slate-400">₹</span>
                            <input
                              type="number"
                              placeholder="0"
                              value={manualAmounts[h.code] !== undefined ? manualAmounts[h.code] : ''}
                              onChange={(e) => handleManualAmountChange(h.code, e.target.value)}
                              className="w-24 px-2 py-1 border border-slate-300 rounded text-xs font-mono text-right focus:border-purple-500 outline-none"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Employer Contributions Section */}
                  <div className="p-4 bg-blue-50/30">
                    <p className="text-xs uppercase font-semibold text-blue-800 mb-2 tracking-wider">Employer Contributions ({employerHeads.length} heads)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {employerHeads.map(h => (
                        <div key={h.code} className="flex items-center justify-between bg-white p-2 border border-blue-200 rounded-lg">
                          <div className="min-w-0 pr-2">
                            <p className="text-xs font-semibold text-slate-800 truncate">{h.name}</p>
                            <p className="text-[10px] font-mono text-blue-500">{h.code}</p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className="text-xs text-slate-400">₹</span>
                            <input
                              type="number"
                              placeholder="0"
                              value={manualAmounts[h.code] !== undefined ? manualAmounts[h.code] : ''}
                              onChange={(e) => handleManualAmountChange(h.code, e.target.value)}
                              className="w-24 px-2 py-1 border border-slate-300 rounded text-xs font-mono text-right focus:border-blue-500 outline-none"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Employee Deductions Section */}
                  <div className="p-4 bg-red-50/30">
                    <p className="text-xs uppercase font-semibold text-red-800 mb-2 tracking-wider">Employee Deductions ({deductionHeads.length} heads)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {deductionHeads.map(h => (
                        <div key={h.code} className="flex items-center justify-between bg-white p-2 border border-red-200 rounded-lg">
                          <div className="min-w-0 pr-2">
                            <p className="text-xs font-semibold text-slate-800 truncate">{h.name}</p>
                            <p className="text-[10px] font-mono text-red-500">{h.code}</p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className="text-xs text-slate-400">₹</span>
                            <input
                              type="number"
                              placeholder="0"
                              value={manualAmounts[h.code] !== undefined ? manualAmounts[h.code] : ''}
                              onChange={(e) => handleManualAmountChange(h.code, e.target.value)}
                              className="w-24 px-2 py-1 border border-slate-300 rounded text-xs font-mono text-right focus:border-red-500 outline-none"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : breakdown ? (
              /* TEMPLATE BREAKDOWN RESULT */
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                  <div className="grid grid-cols-5 gap-3">
                    <div className="text-center"><p className="text-xs text-slate-500">Annual CTC</p><p className="text-lg font-bold text-slate-900">₹{breakdown.ctc_annual?.toLocaleString('en-IN')}</p></div>
                    <div className="text-center"><p className="text-xs text-slate-500">Monthly Gross</p><p className="text-lg font-bold text-emerald-600">₹{breakdown.gross_monthly?.toLocaleString('en-IN')}</p></div>
                    <div className="text-center" title="Employee deductions"><p className="text-xs text-slate-500">Employee Deductions</p><p className="text-lg font-bold text-red-500">₹{breakdown.total_deductions_monthly?.toLocaleString('en-IN')}</p></div>
                    <div className="text-center" title="Employer Contributions"><p className="text-xs text-slate-500">Employer Contrib.</p><p className="text-lg font-bold text-blue-600">₹{(breakdown.total_employer_monthly || 0).toLocaleString('en-IN')}</p></div>
                    <div className="text-center"><p className="text-xs text-slate-500">Net Monthly</p><p className="text-lg font-bold text-[#0052FF]">₹{breakdown.net_monthly?.toLocaleString('en-IN')}</p></div>
                  </div>
                  {breakdown.ctc_residual_annual !== undefined && Math.abs(breakdown.ctc_residual_annual) > 1 && (
                    <p className="text-xs text-amber-600 mt-2 text-center">
                      ⚠ CTC residual: ₹{breakdown.ctc_residual_annual?.toLocaleString('en-IN')}/yr — structure does not fully cover the CTC.
                    </p>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600"><tr>
                      <th className="px-4 py-2 text-left font-medium">Component</th>
                      <th className="px-4 py-2 text-left font-medium">Type</th>
                      <th className="px-4 py-2 text-right font-medium">Monthly</th>
                      <th className="px-4 py-2 text-right font-medium">Annual</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {breakdown.components?.map((c, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-2 text-slate-800">{c.head_name} <span className="text-slate-400 font-mono text-xs">({c.head_code})</span></td>
                          <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded text-xs ${c.head_type === 'earning' ? 'bg-green-50 text-green-700' : c.head_type === 'deduction' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>{c.head_type}</span></td>
                          <td className="px-4 py-2 text-right font-mono text-slate-700">₹{c.monthly_amount?.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-2 text-right font-mono text-slate-700">₹{c.annual_amount?.toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 flex flex-col items-center justify-center text-center shadow-sm">
                <Calculator className="w-12 h-12 text-slate-300 mb-4" />
                <h3 className="text-base font-semibold text-slate-600">CTC Breakdown</h3>
                <p className="text-sm text-slate-400 mt-1">Select a structure template or choose <strong>Manual</strong> to enter custom salary heads.</p>
              </div>
            )}

            {/* Existing CTC Assignments Table */}
            {employeeCTCs.length > 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-testid="recent-ctc-assignments">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-slate-800">Recent CTC Assignments</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Live records from the database showing active & manual structure assignments.</p>
                  </div>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-mono">{employeeCTCs.length} records</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600"><tr>
                      <th className="px-4 py-2 text-left font-medium">Employee</th>
                      <th className="px-4 py-2 text-left font-medium">Structure</th>
                      <th className="px-4 py-2 text-right font-medium">Annual CTC</th>
                      <th className="px-4 py-2 text-right font-medium">Monthly Gross</th>
                      <th className="px-4 py-2 text-left font-medium">Effective Date</th>
                      <th className="px-4 py-2 text-center font-medium">Actions</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {employeeCTCs.slice(0, 15).map((c) => {
                        const emp = employees.find(e => e.id === c.employee_id || e.employment_details?.employee_code === c.employee_code);
                        const name = emp
                          ? (emp.personal_details?.full_name
                              || [emp.personal_details?.first_name, emp.personal_details?.last_name].filter(Boolean).join(' '))
                          : null;
                        const code = c.employee_code || emp?.employment_details?.employee_code || '—';
                        const isManual = c.structure_code === 'MANUAL' || c.structure_source === 'manual';

                        return (
                          <tr key={c.id || c._id} className="hover:bg-slate-50" data-testid={`ctc-row-${code}`}>
                            <td className="px-4 py-2 text-slate-800">
                              <div className="font-medium text-slate-900">{name || code}</div>
                              {name && <div className="text-[11px] text-slate-500 font-mono">{code}</div>}
                            </td>
                            <td className="px-4 py-2">
                              {isManual ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded border border-purple-200">
                                  ✨ MANUAL
                                </span>
                              ) : (
                                <span className="font-mono text-slate-700">{c.structure_code || '—'}</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right font-mono font-medium">₹{c.ctc_annual?.toLocaleString('en-IN') || '—'}</td>
                            <td className="px-4 py-2 text-right font-mono text-emerald-600 font-medium">₹{(c.gross_monthly || (c.ctc_monthly)).toLocaleString('en-IN') || '—'}</td>
                            <td className="px-4 py-2 text-slate-600 font-mono text-xs">{c.effective_from || '—'}</td>
                            <td className="px-4 py-2 text-center">
                              <button
                                onClick={() => handleOpenCTCDetails(c)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded font-medium border border-slate-200 transition-colors"
                              >
                                <Eye className="w-3.5 h-3.5 text-slate-500" /> View Breakup
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center" data-testid="recent-ctc-empty">
                <p className="text-sm text-slate-700 font-medium">No CTC assignments yet for this company.</p>
                <p className="text-xs text-slate-500 mt-1">Use <strong>Manual</strong> mode or assign via structure templates.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTC BREAKDOWN & HISTORY MODAL */}
      {viewingCTC && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setViewingCTC(null)}>
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-slate-50">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <span>CTC Breakup & History Details</span>
                  {(viewingCTC.structure_code === 'MANUAL' || viewingCTC.structure_source === 'manual') && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded border border-purple-200">
                      MANUAL STRUCTURE
                    </span>
                  )}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Employee ID: <span className="font-mono">{viewingCTC.employee_code || viewingCTC.employee_id}</span> · Effective from {viewingCTC.effective_from}
                </p>
              </div>
              <button onClick={() => setViewingCTC(null)} className="text-slate-400 hover:text-slate-700 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-5 space-y-5">
              {/* Totals Summary */}
              <div className="grid grid-cols-4 gap-3 bg-slate-50 p-3 rounded-lg border border-slate-200 text-center">
                <div><p className="text-[11px] text-slate-500">Annual CTC</p><p className="text-sm font-bold font-mono text-slate-900">₹{viewingCTC.ctc_annual?.toLocaleString('en-IN')}</p></div>
                <div><p className="text-[11px] text-slate-500">Monthly Gross</p><p className="text-sm font-bold font-mono text-emerald-600">₹{(viewingCTC.gross_monthly || Math.round(viewingCTC.ctc_monthly)).toLocaleString('en-IN')}</p></div>
                <div><p className="text-[11px] text-slate-500">Total Deductions</p><p className="text-sm font-bold font-mono text-red-500">₹{(viewingCTC.total_deductions_monthly || 0).toLocaleString('en-IN')}</p></div>
                <div><p className="text-[11px] text-slate-500">Net Pay</p><p className="text-sm font-bold font-mono text-[#0052FF]">₹{(viewingCTC.net_monthly || 0).toLocaleString('en-IN')}</p></div>
              </div>

              {/* Head Breakdown Table */}
              <div>
                <h4 className="text-xs uppercase font-semibold text-slate-700 tracking-wider mb-2">Salary Head Breakup</h4>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
                      <tr>
                        <th className="px-3 py-2 text-left">Head Code</th>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Category</th>
                        <th className="px-3 py-2 text-right">Monthly (₹)</th>
                        <th className="px-3 py-2 text-right">Annual (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(viewingCTC.components || []).map((c, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-mono text-xs text-slate-900 font-semibold">{c.head_code}</td>
                          <td className="px-3 py-2 text-slate-800">{c.head_name || c.head_code}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                              c.head_type === 'earning' ? 'bg-green-100 text-green-700' :
                              c.head_type === 'deduction' ? 'bg-red-100 text-red-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {c.head_type || 'earning'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-medium">{c.monthly_amount?.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 text-right font-mono text-slate-600">{c.annual_amount?.toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                      {(!viewingCTC.components || viewingCTC.components.length === 0) && (
                        <tr><td colSpan={5} className="py-4 text-center text-xs text-slate-400">No explicit head components saved in this assignment.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Version History Timeline */}
              <div>
                <h4 className="text-xs uppercase font-semibold text-slate-700 tracking-wider mb-2 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-slate-500" /> Historical Version Timeline ({ctcHistory.length})
                </h4>
                {loadingHistory ? (
                  <p className="text-xs text-slate-400 py-2">Loading history...</p>
                ) : (
                  <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50/50 max-h-40 overflow-y-auto">
                    {ctcHistory.map((h, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs bg-white p-2 rounded border border-slate-200">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${h.is_active !== false ? 'bg-emerald-100 text-emerald-800 font-bold' : 'bg-slate-100 text-slate-600'}`}>
                            {h.is_active !== false ? 'ACTIVE' : 'HISTORICAL'}
                          </span>
                          <span className="font-mono text-slate-700">{h.structure_code || 'MANUAL'}</span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-600 font-mono">From: {h.effective_from}</span>
                        </div>
                        <div className="font-mono font-semibold text-slate-900">
                          ₹{h.ctc_annual?.toLocaleString('en-IN')}/yr
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => handlePopulateManualForEdit(viewingCTC)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium transition-colors"
              >
                <Edit className="w-3.5 h-3.5" /> Revise / Edit as Manual Structure
              </button>
              <button onClick={() => setViewingCTC(null)} className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-medium">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <BulkUploadModal
        open={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        title="Bulk Assign CTC"
        description="Pick a default salary structure below (or specify per-row via the structure_code column in the CSV). Every row gets its structure automatically applied so the payroll register shows the full breakup."
        templateUrl="/payroll/employee-ctc/bulk-template"
        uploadUrl="/payroll/employee-ctc/bulk-upload"
        uploadParams={selectedCompany ? { company_id: selectedCompany } : {}}
        templateFileName="employee_ctc_bulk_template.csv"
        columns={['employee_code', 'structure_code (optional)', 'ctc_annual', 'effective_from', 'effective_to']}
        supportsDryRun
        headerControl={{
          label: 'Salary Structure (from Masters)',
          paramName: 'structure_code',
          required: true,
          value: bulkStructure,
          onChange: setBulkStructure,
          options: [
            ...(structures || []).map(s => ({ value: s.code, label: `${s.code} — ${s.name}${s.is_default ? '  (default)' : ''}` })),
            { value: 'MANUAL', label: 'MANUAL — Per-row / Custom breakup' }
          ],
          help: 'Applied to every row unless the CSV has a structure_code column filled in.'
        }}
        onUploadSuccess={() => { setShowBulkUpload(false); fetchEmployeeCTCs(); fetchUnmapped(); }}
      />
    </MainLayout>
  );
};

export default CTCPlanner;
